/**
 * An event's console, full window.
 *
 * A slim omnium bar on top — back to the event, the event's name, the workflow
 * and its version, a live dot — and the event's operations app in a frame below.
 * The app is the default `operate` app from GET /apps/choices; `?app=<code>`
 * opens another one that is mapped to the event's workflow.
 */

import { Moon, Sun } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { createHttpApi } from "@fanos/omnium-bridge";

import { AppFrame, FrameState, LiveDot, type FrameInfo } from "@/components/bridge/AppFrame";
import { Button, LinkButton } from "@/components/kit";
import { getToken } from "@/lib/api/auth";
import { getAdminApiBaseUrl } from "@/lib/api/config";
import { choiceKey, pickChoice, problemFor } from "@/lib/bridge/frame";
import { otherTheme, setTheme } from "@/lib/theme";
import { useApi } from "@/spa/useApi";
import { useTheme } from "@/spa/useTheme";

import styles from "./EventConsolePage.module.css";

export function EventConsolePage() {
  const { code = "" } = useParams<{ code: string }>();
  const [params] = useSearchParams();
  const theme = useTheme();

  const choices = useApi(
    () =>
      createHttpApi({ baseUrl: getAdminApiBaseUrl(), token: getToken }).choices({
        kind: "event",
        code,
      }),
    [code],
  );
  const chosen = choices.data
    ? pickChoice(choices.data, "operate", { app: params.get("app"), workflow: params.get("workflow") })
    : null;

  const bar = ({ session, live }: FrameInfo) => (
    <header className={styles.bar}>
      <Link className={styles.back} to={`/events/${encodeURIComponent(code)}`}>
        ← Event
      </Link>
      <h1 className={styles.title}>{session?.subject.name ?? code}</h1>
      {session && (
        <span className={styles.workflow}>
          {session.workflow.name}
          {session.workflow.version !== null ? ` · v${session.workflow.version}` : " · not published"}
        </span>
      )}
      <span className={styles.push} />
      <LiveDot live={live} />
      <button
        type="button"
        className={styles.themeButton}
        onClick={() => setTheme(otherTheme(theme))}
        aria-label={theme === "dark" ? "Show the app in light" : "Show the app in dark"}
        title={theme === "dark" ? "Show the app in light" : "Show the app in dark"}
      >
        {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
      </button>
    </header>
  );

  let body;
  if (choices.error) {
    const problem = problemFor(choices.cause);
    body = (
      <>
        {bar({ session: null, live: { tone: "off", label: "Not connected" } })}
        <FrameState title={problem.title} note={problem.note}>
          {problem.retry && (
            <Button tone="primary" onClick={choices.reload}>
              Try again
            </Button>
          )}
        </FrameState>
      </>
    );
  } else if (!choices.data) {
    body = (
      <>
        {bar({ session: null, live: { tone: "wait", label: "Opening" } })}
        <FrameState title="Opening…" />
      </>
    );
  } else if (!chosen) {
    body = (
      <>
        {bar({ session: null, live: { tone: "off", label: "Not connected" } })}
        <FrameState
          title="No app is set up to run this event"
          note="Map an operations app to this event's workflow, then open the console again."
        >
          <LinkButton to="/workflows" tone="primary">
            Open workflows
          </LinkButton>
        </FrameState>
      </>
    );
  } else {
    body = (
      <AppFrame
        key={choiceKey(chosen)}
        app={chosen.app.code}
        workflow={chosen.workflow.code}
        subject={{ kind: "event", code }}
        event={code}
        bar={bar}
      />
    );
  }

  return (
    <div className={styles.page} data-theme={theme}>
      {body}
    </div>
  );
}
